import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import WorkEntryForm from './WorkEntryForm';

export default function AdminEntries() {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchEntries();
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [selectedUserId]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchEntries = async () => {
    try {
      const params = selectedUserId ? `?user_id=${selectedUserId}` : '';
      const response = await axios.get(`${API}/admin/entries${params}`);
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to fetch entries');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('DELETE THIS ENTRY? [Y/N]')) return;

    try {
      await axios.delete(`${API}/admin/entries/${entryId}`);
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingEntry(null);
    fetchEntries();
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.username : 'Unknown';
  };

  if (loading) {
    return (
      <div className="retro-panel text-center py-12">
        <div className="text-2xl glow-text" style={{ color: '#FF6B00' }}>
          [LOADING ENTRIES...]
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="retro-panel mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="flex-1">
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              Filter by User:
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              data-testid="filter-user-select"
              className="retro-input"
              style={{ minWidth: '200px' }}
            >
              <option value="">ALL USERS</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.username}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {showForm && (
        <div className="mb-6">
          <WorkEntryForm
            entry={editingEntry}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setShowForm(false);
              setEditingEntry(null);
            }}
            isAdmin={true}
          />
        </div>
      )}

      <div className="retro-panel overflow-x-auto">
        <h2 className="text-3xl font-bold mb-6 glow-text" style={{ color: '#FF6B00', fontFamily: 'Orbitron, sans-serif' }}>
          [ALL WORK ENTRIES] - TOTAL: {entries.length}
        </h2>

        {entries.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-2xl glow-text" style={{ color: '#ffffff' }}>
              [NO ENTRIES FOUND]
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="retro-table" data-testid="admin-entries-table">
              <thead>
                <tr>
                  <th>USER</th>
                  <th>DATE</th>
                  <th>DAY</th>
                  <th>START</th>
                  <th>STOP</th>
                  <th>CUSTOMER</th>
                  <th>JOB</th>
                  <th>HOURS</th>
                  <th>TYPE</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td style={{ color: '#FF6B00', fontWeight: 700 }}>{getUserName(entry.user_id)}</td>
                    <td>{entry.date}</td>
                    <td>{entry.day}</td>
                    <td style={{ color: '#FF6B00' }}>{entry.start_time}</td>
                    <td style={{ color: '#FF6B00' }}>{entry.stop_time}</td>
                    <td style={{ color: '#ffffff', fontWeight: 700 }}>{entry.customer_name}</td>
                    <td style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{entry.job_description.substring(0, 30)}...</td>
                    <td style={{ color: '#FF6B00', fontWeight: 700 }}>{entry.hours_worked}h</td>
                    <td style={{ color: entry.work_type === 'remote' ? '#ffffff' : '#FF6B00', fontWeight: 700 }}>
                      {entry.work_type.toUpperCase()}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(entry)}
                          data-testid={`edit-admin-entry-${entry.id}`}
                          className="px-3 py-1 text-sm border-2 font-semibold"
                          style={{
                            color: '#ffffff',
                            borderColor: '#ffffff',
                            background: 'transparent',
                            boxShadow: '0 0 5px rgba(255, 255, 255, 0.3)'
                          }}
                        >
                          EDIT
                        </button>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          data-testid={`delete-admin-entry-${entry.id}`}
                          className="px-3 py-1 text-sm border-2 font-semibold"
                          style={{
                            color: '#FF6B00',
                            borderColor: '#FF6B00',
                            background: 'transparent',
                            boxShadow: '0 0 5px rgba(255, 107, 0, 0.3)'
                          }}
                        >
                          DEL
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}